import { useState } from 'react'
import { AlsoInOwnership } from '@/features/ownership/AlsoInOwnership'
import { useCopy } from '@/shared/ui/useCopy'
import type { ContactWithAwareness } from '@/types/crm'

/**
 * Contacts, first one open and the rest folded away.
 *
 * A lead with a Skida PRO roster now carries six, seventeen, twenty-seven
 * people. Listing them all put the email and the activity log below a wall of
 * addresses that a rep reads once and never again — so only the primary
 * contact is shown, and the rest are one click behind a count.
 *
 * The primary is whoever is first in the array, which is not arbitrary: the PRO
 * admin is hoisted to position zero when the roster is linked, because they are
 * the person who can buy seats for everyone else.
 */
export function ContactList({
  contacts,
  onAdd,
  onEdit,
  onRemove,
  busy,
}: {
  contacts: ContactWithAwareness[]
  onAdd: () => void
  onEdit: (index: number) => void
  onRemove: (index: number) => void
  busy: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  /**
   * Which row is asking to be confirmed, inline.
   *
   * Replaced a `window.confirm`. Persona testing flagged native dialogs as
   * jarring against the inline validation everywhere else in this app, and they
   * cannot be styled or reached in an embed. Two clicks on the same button say
   * the same thing without leaving the page.
   */
  const [armed, setArmed] = useState<number | null>(null)
  const { copied, copy } = useCopy()

  if (contacts.length === 0) {
    return (
      <div className="clist clist--empty">
        <span>No contacts yet — nobody to send anything to.</span>
        <button className="btn-mini" onClick={onAdd}>
          + Add contact
        </button>
      </div>
    )
  }

  const shown = expanded ? contacts : contacts.slice(0, 1)
  const hidden = contacts.length - shown.length

  return (
    <div className="clist">
      {shown.map((c, i) => (
        <div className="crow" key={`${c.email}-${i}`}>
          <div className="crow__who">
            <div className="crow__name">
              {c.name || '(no name)'}
              {i === 0 && contacts.length > 1 && <span className="crow__primary">primary</span>}
            </div>
            {c.role && <div className="crow__role">{c.role}</div>}
            {/* Decision 74. A Person holder links to a lead *contact*, and a
                named person is the likelier of the two to be on the register —
                so the fact belongs on the row somebody actually looks at, not
                only on the lead header and not only in Ownership's own LINKED
                TO column. Admin-only: the component renders nothing at all for
                anybody else, rather than a gap where a tag would be. */}
            {c.email && <AlsoInOwnership contactEmail={c.email} />}
          </div>

          <div className="crow__reach">
            {c.email && (
              <button
                className="crow__email"
                onClick={() => void copy(`e${i}`, c.email)}
                title="Click to copy"
              >
                {copied === `e${i}` ? '✓ copied' : c.email}
              </button>
            )}
            {c.phone && <span className="crow__phone">{c.phone}</span>}
            {(c.awareness?.isConsumerUser || c.knownSkidaUser) && (
              <span className="crow__skida">
                Skida user{c.awareness?.since ? ` since ${c.awareness.since}` : ''}
              </span>
            )}
          </div>

          <div className="crow__actions">
            <button className="btn-mini" onClick={() => onEdit(i)} disabled={busy}>
              Edit
            </button>
            <button
              className={`btn-mini btn-mini--danger${armed === i ? ' btn-mini--armed' : ''}`}
              onClick={() => (armed === i ? onRemove(i) : setArmed(i))}
              onBlur={() => armed === i && setArmed(null)}
              disabled={busy}
            >
              {armed === i ? 'Remove?' : 'Remove'}
            </button>
          </div>
        </div>
      ))}

      <div className="clist__foot">
        {hidden > 0 && (
          <button className="linkbtn" onClick={() => setExpanded(true)}>
            Show {hidden} more contact{hidden === 1 ? '' : 's'}
          </button>
        )}
        {expanded && contacts.length > 1 && (
          <button className="linkbtn" onClick={() => setExpanded(false)}>
            Show less
          </button>
        )}
        <button className="btn-mini" onClick={onAdd}>
          + Add contact
        </button>
      </div>
    </div>
  )
}
